import { nanoid } from 'nanoid';
import { createAuthedClient } from '@/lib/supabase/client';
import { notifyAssetLibraryChanged } from '@/components/canvas/utils/asset-folders-events';

type UploadOptions = {
  token: string;
  userId: string;
  filename?: string;
  /** When uploading project-scoped assets, store under `${userId}/${projectId}/{image|video|audio}/...` */
  projectId?: string;
  /** Clerk org id — when set, the asset is registered under the team library */
  orgId?: string;
  assetType?: 'image' | 'video' | 'audio' | 'file';
  metadata?: Record<string, unknown> | null;
};

async function registerFileAsset(params: {
  storagePath: string;
  publicUrl: string;
  type: string;
  name: string;
  mode?: string | null;
  projectId?: string | null;
  orgId?: string | null;
  contentType?: string | null;
  size?: number | null;
  metadata?: Record<string, unknown> | null;
}) {
  try {
    const res = await fetch('/api/library', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (res.ok) notifyAssetLibraryChanged();
  } catch {}
}

function detectModeFromFilename(name: string): string | null {
  const lc = name.toLowerCase();
  if (lc.startsWith('text2image_')) return 'text2image';
  if (lc.startsWith('image2image_')) return 'image2image';
  if (lc.startsWith('text2video_')) return 'text2video';
  if (lc.startsWith('image2video_')) return 'image2video';
  if (lc.startsWith('video2video_')) return 'video2video';
  if (lc.startsWith('text2audio_')) return 'text2audio';
  if (lc.startsWith('audio2audio_')) return 'audio2audio';
  if (lc.startsWith('upload_')) return 'upload';
  if (lc.startsWith('mask_')) return 'mask';
  return null;
}

function readMediaInfo(file: File): Promise<{ resolution?: string; duration?: number } | null> {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const mt = String(file.type || '').toLowerCase();
      if (mt.startsWith('image/')) {
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ resolution: `${img.naturalWidth}x${img.naturalHeight}` }); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      } else if (mt.startsWith('video/')) {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => {
          URL.revokeObjectURL(url);
          const dur = Number.isFinite(vid.duration) ? Math.round(vid.duration) : undefined;
          resolve({ resolution: `${vid.videoWidth}x${vid.videoHeight}`, duration: dur });
        };
        vid.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        vid.src = url;
      } else {
        URL.revokeObjectURL(url);
        resolve(null);
      }
    } catch { resolve(null); }
  });
}

let providerCache: Promise<'supabase' | 'oss'> | undefined;

async function getUploadProvider(): Promise<'supabase' | 'oss'> {
  if (!providerCache) {
    providerCache = fetch('/api/app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: 'upload-config' }),
    })
      .then(async (r) => {
        const data = (await r.json().catch(() => ({}))) as any;
        if (!r.ok) return 'supabase';
        return data?.provider === 'oss' ? 'oss' : 'supabase';
      })
      .catch(() => 'supabase');
  }
  return providerCache;
}

export const uploadFile = async (
  file: File,
  bucket: 'avatars' | 'files' | 'screenshots',
  options: UploadOptions
) => {
  const userId = options.userId;
  if (!userId) {
    throw new Error('You need to be logged in to upload a file!');
  }

  const provider = await getUploadProvider();

  const inferredAssetType = (() => {
    try {
      const mt = String(file?.type || '').toLowerCase();
      if (mt.startsWith('image/')) return 'image' as const;
      if (mt.startsWith('video/')) return 'video' as const;
      if (mt.startsWith('audio/')) return 'audio' as const;
      return undefined;
    } catch {
      return undefined;
    }
  })();
  const assetType = options?.assetType || inferredAssetType;
  const safeProjectId = (() => {
    const raw = String(options?.projectId || '').trim();
    if (!raw) return '';
    return raw.replace(/[\/\\]/g, '_');
  })();
  // Project-scoped assets should live under: `${userId}/${projectId}/{image|video|audio|file}/...`
  // If projectId is missing (e.g. non-project uploads), we keep legacy behavior by uploading to user root.
  const dir = bucket === 'files' && assetType && safeProjectId ? `${safeProjectId}/${assetType}` : '';

  let uploadMeta: Record<string, unknown> | null = options?.metadata || null;
  if (bucket === 'files' && (assetType === 'image' || assetType === 'video')) {
    try {
      const info = await readMediaInfo(file);
      if (info) {
        uploadMeta = { ...uploadMeta, mode: 'upload', ...info };
      }
    } catch {}
  }

  // --- Direct-to-OSS: browser -> OSS, server only issues short-lived policy ---
  if (provider === 'oss') {
    const ext = (() => {
      const name = String(file.name || '');
      const idx = name.lastIndexOf('.');
      const e = idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
      return e && e.length <= 8 ? e : 'bin';
    })();
    const filename = options?.filename || `upload_${nanoid()}.${ext}`;

    const policyRes = await fetch('/api/app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        app: 'oss-policy',
        bucket,
        dir: dir || undefined,
        filename,
        contentType: file.type || 'application/octet-stream',
      }),
    });

    const policyData = (await policyRes.json().catch(() => ({}))) as any;
    if (!policyRes.ok) {
      throw new Error(policyData?.error || `OSS policy failed (${policyRes.status})`);
    }

    const uploadUrl = String(policyData?.uploadUrl || '');
    const url = String(policyData?.url || '');
    const formFields = (policyData?.formFields || {}) as Record<string, string>;
    if (!uploadUrl || !url || !formFields?.key) {
      throw new Error('OSS policy response invalid');
    }

    const fd = new FormData();
    for (const [k, v] of Object.entries(formFields)) {
      if (typeof v === 'string') fd.set(k, v);
    }
    fd.set('file', file);

    const uploadResp = await fetch(uploadUrl, { method: 'POST', body: fd });
    if (!uploadResp.ok) {
      const text = await uploadResp.text().catch(() => '');
      throw new Error(`OSS upload failed (${uploadResp.status}) ${text}`.trim());
    }

    if (bucket === 'files' && assetType && assetType !== 'file') {
      const storagePath = `${userId}/${dir ? `${dir}/` : ''}${filename}`;
      registerFileAsset({
        storagePath,
        publicUrl: url,
        type: assetType,
        name: filename,
        mode: detectModeFromFilename(filename),
        projectId: safeProjectId || null,
        orgId: options?.orgId || null,
        contentType: file.type || null,
        size: file.size || null,
        metadata: uploadMeta,
      });
    }

    return { url, type: String(file.type || '') };
  }

  // --- Supabase (default): direct upload from browser -> Supabase Storage ---
  // Note: auth is enforced via Supabase Storage RLS policies; server is not involved in the file transfer.
  const token = options?.token;
  if (!token) throw new Error('Missing Supabase auth token');

  const supabase = createAuthedClient(token);

  const ext = (() => {
    const name = String(file.name || '');
    const idx = name.lastIndexOf('.');
    const e = idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
    return e && e.length <= 8 ? e : 'bin';
  })();

  const filename = options?.filename || `upload_${nanoid()}.${ext}`;
  const objectPath = `${userId}/${dir ? `${dir}/` : ''}${filename}`;
  const contentType = (file.type || 'application/octet-stream').trim() || 'application/octet-stream';

  const upload = await supabase.storage.from(bucket).upload(objectPath, file, {
    contentType,
    upsert: bucket === 'screenshots',
  } as any);

  if ((upload as any)?.error) {
    throw new Error((upload as any).error?.message || 'UPLOAD_FAILED');
  }

  const path = (upload as any)?.data?.path as string | undefined;
  if (!path) throw new Error('UPLOAD_FAILED');

  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
  const url = pub?.publicUrl;
  if (!url) throw new Error('PUBLIC_URL_FAILED');

  if (bucket === 'files' && assetType && assetType !== 'file') {
    registerFileAsset({
      storagePath: objectPath,
      publicUrl: url,
      type: assetType,
      name: filename,
      mode: detectModeFromFilename(filename),
      projectId: safeProjectId || null,
      orgId: options?.orgId || null,
      contentType: file.type || null,
      size: file.size || null,
      metadata: uploadMeta,
    });
  }

  return { url, type: String(file.type || '') };
};
